import * as React from "react";
import { Box, Typography } from "@mui/material";
import { useDispatch } from "react-redux";
import List, { EmptyView } from "../../components/OList";
import * as reportsApi from "../../api/reports";
import { tl, t } from "../../components/translate";
import useMobileScreen from "../../hooks/useMobileScreen";
import "./Reports.scss";
import Filters, { FilterProps } from "./Filters";
import { convertADtoBS } from "../../components/Calendar/functions/calendarFunctions";
import * as NotificationActions from "../../actions/notification";
import useIsReactNativeWebView from "../../hooks/useIsReactNativeWebView";

const CustomerDueReport = (): React.ReactNode => {
  const dispatch = useDispatch();
  const isMobileScreen = useMobileScreen();
  const { isRnWebView } = useIsReactNativeWebView();

  // local states
  const [reportData, setReportData] = React.useState([]);
  const [isRequestLoading, setIsRequestLoading] = React.useState(false);
  const [filters, setFilters] = React.useState<FilterProps>({
    clientIds: []
  } as FilterProps);

  // effects
  React.useEffect(() => {
    (async () => {
      try {
        setIsRequestLoading(true);
        const response = await reportsApi.getCustomerDueReport(filters);
        setReportData(response);
      } catch (error: any) {
        dispatch(
          NotificationActions.notificationAdd({
            id: new Date().getUTCMilliseconds(),
            variant: "error",
            message: error?.message || "Sorry ! Something Went Wrong.",
            autoTimeout: true
          })
        );
      } finally {
        setIsRequestLoading(false);
      }
    })();
  }, [filters]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ px: 3 }}>
        <Filters
          showPeriod={false}
          filters={filters as FilterProps}
          onSetFilters={(newFiters) => setFilters(newFiters)}
          isRequestLoading={isRequestLoading}
        />
      </Box>

      <Box
        className="salesReportList"
        sx={{
          borderTop: 1,
          borderColor: "divider",
          width: "100%",
          overflowX: "auto"
        }}
      >
        <Box sx={{ minWidth: isMobileScreen || isRnWebView ? "900px" : "auto" }}>
          <List
            automation="salesReportList"
            data={reportData}
            rowHeight={50}
            defaultSortColumn="billingDate"
            defaultSortOrder={-1}
            activeRow={0}
            adjustHeightToContents
            columns={[
              {
                key: "clientName",
                label: tl("reports.client"),
                sortable: true,
                formatter: ({ client }) => (
                  <Box
                    style={{
                      width: "150px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis"
                    }}
                  >
                    {`${client.firstName} ${client.lastName}
                ${client.active ? "" : "(deleted)"}`}
                  </Box>
                )
              },
              {
                key: "transactionDate",
                label: "transactionDate",
                sortable: true,
                formatter: ({ transactionDate }) => (
                  <Typography>{convertADtoBS(new Date(transactionDate)).formatted4}</Typography>
                )
              },
              {
                key: "balance",
                label: "Due",
                sortable: true,
                formatter: ({ balance }) => <Typography>{Math.abs(balance)}</Typography>
              }
            ]}
          >
            <EmptyView>
              <Box textAlign="center" padding="50px">
                No items to show.
              </Box>
            </EmptyView>
          </List>
          <Box className="totalBar">
            <Box display="flex" paddingLeft="20px">
              {t("reports.total")}
            </Box>
            <Box display="flex" flexBasis="230px" justifyContent="flex-end" paddingRight="42px">
              <Typography width="200px">
                Rs: {Math.abs(reportData.reduce((acc, cur) => acc + cur.balance, 0)).toFixed(2)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CustomerDueReport;
